import { atom, PrimitiveAtom } from 'jotai';

// 選択中の計算セットの mtg カウント（ExistingProjects が useCalculationSet 完了後にセット）
export const rCalcSetMtgAtom = atom<number | null>(null);

type Status = 'notstarted' | 'loading' | 'success' | 'error';

export const activeMtgVibGraphTabAtom = atom<number>(0);

export type NullableFloatArray = Array<number | null>;

export type MtgVibData = {
    data: {
        mtg_vib_target_squared_sum: NullableFloatArray;
        vel: NullableFloatArray;
        rpm: NullableFloatArray;
    },
    status: Status,
};

export const allConditionDataAtom = atom<Record<string, MtgVibData>>({});
export const allConditionLoadingAtom = atom<boolean>(false);

// mtg/powertrain によって mount 系列キーが変わるため、固定キーでは持たない。
export type MountSeriesData = Record<string, NullableFloatArray>;

const createEmptyMountSeriesData = (): MountSeriesData => ({});

export type CabinNoiseTargetData = {
    data: {
        mtg_vib_target_squared_sum: NullableFloatArray;
        cabin_noise_target: NullableFloatArray;
        background_noise_target: NullableFloatArray;
        deltaL_target: NullableFloatArray;
    },
    status: Status,
};

export const CabinNoiseTargetAtom = atom<CabinNoiseTargetData>({
    data: {
        mtg_vib_target_squared_sum: [],
        cabin_noise_target: [],
        background_noise_target: [],
        deltaL_target: [],
    },
    status: "notstarted"
});

export type ContributionData = {
    data: MountSeriesData,
    status: Status
};

export const ContributionAtom = atom<ContributionData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type NtfData = {
    data: MountSeriesData,
    status: Status
};

export const NtfAtom = atom<NtfData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgInsuData = {
    data: MountSeriesData,
    status: Status
};

export const MtgInsuAtom = atom<MtgInsuData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgVibCtrTargetData = {
    data: MountSeriesData,
    status: Status
};

export const MtgVibCtrTargetAtom = atom<MtgVibCtrTargetData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgVibTargetData = {
    data: MountSeriesData,
    status: Status
};

export const MtgVibTargetAtom = atom<MtgVibTargetData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type EachConditionDataKeys =
    | 'cabin_noise_target'
    | 'contribution'
    | 'ntf'
    | 'mtg_insu'
    | 'mtg_vib_ctr_target'
    | 'mtg_vib_target';

export type EachConditionData = {
    graph_id: EachConditionDataKeys;
    data: any;
    status: Status;
};

export const eachConditionDataAtomMap: Record<EachConditionDataKeys, PrimitiveAtom<{ data: any; status: Status }>> = {
    cabin_noise_target: CabinNoiseTargetAtom,
    contribution: ContributionAtom,
    ntf: NtfAtom,
    mtg_insu: MtgInsuAtom,
    mtg_vib_ctr_target: MtgVibCtrTargetAtom,
    mtg_vib_target: MtgVibTargetAtom,
};

export const setEachConditionDataAtom = atom<null, EachConditionData[], void>(
    null,
    (_get, set, ...payload: EachConditionData[]) => {
        payload.forEach(({ graph_id, data, status }) => {
            set(eachConditionDataAtomMap[graph_id], { data, status });
        })
    }
);

export const getEachConditionDataAtom = atom<EachConditionData[]>(get => {
    return (Object.keys(eachConditionDataAtomMap) as EachConditionDataKeys[]).map((key) => {
        const { data, status } = get(eachConditionDataAtomMap[key])
        return { graph_id: key, data, status }
    })
});

export const allEachConditionDataErrorAtom = atom<boolean>(get => {
    const list = get(getEachConditionDataAtom)
    return list.every(item => item.status === 'error')
});

export const eachConditionDataByKeyAtom = (key: EachConditionDataKeys) =>
    atom(get => {
        const list = get(getEachConditionDataAtom)
        return list.find((item): item is EachConditionData => item.graph_id === key)
    });
